import clientSide from '~/helpers/clientSide';
import { Address } from '~/types/address';
import { Tracking } from '~/types/common';
import { Contact } from '~/types/contact-credit';
import { SelectedProductsState } from '~/types/products';
import { SessionSummaryResults } from '~/types/session-summary';

// Constants for chatbot action detection and element selection
export const CLICK_DEBOUNCE_MS = 300;

export const ACTION_KEYWORDS = {
  open: ['launch', 'open', 'start', 'chat'],
  close: ['close', 'exit'],
  minimize: ['minimize', 'collapse'],
};

export const ACTION_SYMBOLS = {
  close: ['×', '✕'],
  minimize: ['−', '_'],
};

export const CHATBOT_SELECTORS = [
  '[id*="kore"]',
  '[class*="kore"]',
  '[data-kore-chatbot]',
  '.kore-chat-window',
  '.kore-chat-header',
  '.kore-chat-button',
  '#koreChatBot',
  '.chatWindowDiv',
  '.chatWindow',
  '[id*="chat"]',
  '[class*="chat-bot"]',
  '[class*="chatbot"]',
  '.kore-chat-bubble',
  '[class*="chatwindow"]',
  '[class*="droppable"]',
  '[class*="minimize"]',
  '[class*="cobrowse"]',
  '[data-testid*="chat"]',
  '[data-cy*="chat"]',
  'iframe[src*="kore"]',
  // Script tags
  'script[src*="kore-chatbot"]',
  'script[data-nscript="lazyOnload"][src*="chatbot"]',
];

export const INPUT_SELECTORS = [
  'input[type="text"]',
  'textarea',
  '[class*="input"]',
  '[class*="message"]',
  '[placeholder*="message"]',
  '[placeholder*="type"]',
];

// Types
export interface ElementAttributes {
  className: string;
  id: string;
  title: string;
  ariaLabel: string;
  innerHTML: string;
}

export interface TrackingData {
  action: string;
  data: {
    elementType: string;
    location: string;
    [key: string]: unknown;
  };
}

export interface KoreDataType extends Record<string, unknown> {
  accountId?: string[];
  address?: string;
  selectedProducts?: SelectedProductsState;
  quoteNumber?: string;
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  // NGC Session Summary fields
  recFocus?: string;
  switcherReason?: string | null;
  internetUsage?: string;
  currentSpeed?: string;
  featureInterests?: string;
  propertyInformation?: string;
  previousActivity?: string;
  recommendedPackages?: string;
  // Chatbot Editable Text
}

export interface ChatIconConfig extends Record<string, unknown> {
  position?: {
    bottom?: string;
    right?: string;
  };
  trigger_type?: 'proactive';
  proactive?: {
    message?: string;
    type?: 'default' | 'side_rail';
    display?: 'horizontal' | 'vertical';
  };
}

// Helper functions
export const getElementAttributes = (element: HTMLElement): ElementAttributes => ({
  className: element.className?.toLowerCase() || '',
  id: element.id?.toLowerCase() || '',
  title: element.title?.toLowerCase() || '',
  ariaLabel: element.getAttribute('aria-label')?.toLowerCase() || '',
  innerHTML: element.innerHTML || '',
});

export const getChatAction = (element: HTMLElement): string | null => {
  const { className, id, title, ariaLabel, innerHTML } = getElementAttributes(element);
  const textSources = [className, id, title, ariaLabel];

  // Check each action type against keywords
  for (const [action, keywords] of Object.entries(ACTION_KEYWORDS)) {
    if (keywords.some((keyword) => textSources.some((source) => source.includes(keyword)))) {
      return action;
    }
  }

  // Check symbols for close/minimize actions
  for (const [action, symbols] of Object.entries(ACTION_SYMBOLS)) {
    if (symbols.some((symbol) => innerHTML.includes(symbol))) {
      return action;
    }
  }

  return null;
};

export const createTrackingData = (
  action: string,
  elementType: string,
  location: string,
  additionalData: Record<string, unknown> = {}
): TrackingData => ({
  action,
  data: {
    elementType,
    location,
    ...additionalData,
  },
});

export const collectElements = (selectors: readonly string[]): Set<Element> => {
  const elements = new Set<Element>();
  selectors.forEach((selector) => {
    document.querySelectorAll(selector).forEach((element) => elements.add(element));
  });
  return elements;
};

export const attachEventListeners = (
  elements: Set<Element>,
  eventType: string,
  handler: EventListener,
  trackedElements: Set<Element>
): void => {
  elements.forEach((element) => {
    if (!trackedElements.has(element)) {
      element.addEventListener(eventType, handler);
      trackedElements.add(element);
    }
  });
};

export const filterElementsInChatbot = (elements: Set<Element>): Set<Element> => {
  const filteredElements = new Set<Element>();
  const containerSelector = '[id*="kore"], [class*="kore"], .chatWindowDiv';
  elements.forEach((element) => {
    if (element.closest(containerSelector)) {
      filteredElements.add(element);
    }
  });
  return filteredElements;
};

export const isWithinDebounceWindow = (lastClickTime: number, debounceMs: number = CLICK_DEBOUNCE_MS): boolean => {
  return Date.now() - lastClickTime < debounceMs;
};

export const updateKoreData = (
  tracking?: Tracking,
  address?: { install?: Address },
  products?: { selected?: SelectedProductsState },
  contact?: Contact,
  sessionSummary?: SessionSummaryResults,
): void => {
  if (typeof window !== 'undefined') {
    if (!window.KOREDATA) {
      window.KOREDATA = {};
    }

    const addressString = address?.install?.address
      ? [address.install.address, address.install.city, address.install.state, address.install.zip]
          .filter(Boolean)
          .join(', ')
      : undefined;

    const koreData: KoreDataType = {
      accountId: tracking?.accountUUID ? [tracking.accountUUID] : undefined,
      address: addressString,
      selectedProducts: products?.selected,
      quoteNumber: tracking?.quoteNumber,
      email: contact?.email,
      phone: contact?.phone,
      firstName: contact?.firstname,
      lastName: contact?.lastname,
      recFocus: sessionSummary?.discovery?.recFocus,
      switcherReason: sessionSummary?.discovery?.switcherReason,
      internetUsage: sessionSummary?.discovery?.internetUsage,
      currentSpeed: sessionSummary?.discovery?.currentSpeed,
      featureInterests: sessionSummary?.discovery?.featureInterests,
      propertyInformation: sessionSummary?.propertyInformation,
      previousActivity: sessionSummary?.previousActivity,
      recommendedPackages: sessionSummary?.recommendedPackages,
    };

    window.KOREDATA = {
      ...window.KOREDATA,
      ...koreData,
    };

    if (window.KoreChatSDK) {
      if (typeof window.KoreChatSDK.updateCustomData === 'function') {
        window.KoreChatSDK.updateCustomData(window.KOREDATA);
      } else if (
        window.KoreChatSDK.chatWindowInstance &&
        typeof window.KoreChatSDK.chatWindowInstance.updateCustomData === 'function'
      ) {
        window.KoreChatSDK.chatWindowInstance.updateCustomData(window.KOREDATA);
      } else if (window.KoreChatSDK.chatWindowInstance && window.KoreChatSDK.chatWindowInstance._botInfo) {
        window.KoreChatSDK.chatWindowInstance._botInfo.customData = {
          ...window.KoreChatSDK.chatWindowInstance._botInfo.customData,
          ...window.KOREDATA,
        };
      }
    }
  }
};

export const cleanupChatbot = (): void => {
  if (clientSide) {
    if (window?.KoreChatSDK) {
      try {
        // Stop any pending operations
        if (window.KoreChatSDK.chatWindowInstance) {
          window.KoreChatSDK.chatWindowInstance.destroy?.();
        }
        if (typeof window?.KoreChatSDK.destroy === 'function') {
          window?.KoreChatSDK.destroy();
        }
      } catch (error) {
        // Continue
      }
    }

    // Remove all chatbot elements
    CHATBOT_SELECTORS.forEach((selector) => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((element) => {
        element.remove();
      });
    });

    // Clean up window properties
    Object.keys(window).forEach((key) => {
      if (key.toLowerCase().includes('kore') && key !== 'KOREDATA') {
        try {
          delete window[key];
        } catch (e) {
          window[key] = undefined;
        }
      }
    });

    // Force remove any style tags added by the SDK
    document.querySelectorAll('style').forEach((style) => {
      if (style.innerHTML.includes('kore') || style.innerHTML.includes('chat')) {
        style.remove();
      }
    });
  }
};

export const hideChatbot = (): void => {
  if (clientSide) {
    const koreChatWindow = document.querySelector('[id*="kore-chat-window"]');
    if (koreChatWindow) {
      (koreChatWindow as HTMLElement).style.display = 'none';
    }
  }
};

/**
 * Programmatically clicks the chatbot launch button or main window.
 * Returns true if a clickable element was found and clicked, false otherwise.
 */
export const triggerChatbotClick = (): boolean => {
  if (!clientSide) return false;

  // First, try to find an element with a "minimized" class that is a child of kore-chat-window
  const minimizedEls = document.querySelectorAll('[class*="minimized"]');
  for (const minimizedEl of Array.from(minimizedEls)) {
    if (
      minimizedEl instanceof HTMLElement &&
      minimizedEl.closest('[id*="kore-chat-window"], [class*="kore-chat-window"]')
    ) {
      minimizedEl.click();
      return true;
    }
  }

  // Fallback to existing selectors
  const preferredSelectors = [
    '.kore-chat-button',
    '[data-kore-chatbot]',
    '.kore-chat-window',
    '#koreChatBot',
    '.chatWindowDiv',
    '[class*="chatbot"]',
    ...CHATBOT_SELECTORS,
  ];
  for (const selector of preferredSelectors) {
    const el = document.querySelector(selector);
    if (el && el instanceof HTMLElement) {
      el.click();
      return true;
    }
  }
  return false;
};

export const showChatbot = (): void => {
  if (clientSide) {
    const koreChatWindow = document.querySelector('[id*="kore-chat-window"]');
    if (koreChatWindow) {
      (koreChatWindow as HTMLElement).style.display = 'block';
    }
  }
};

export const showProactiveIcon = (message: string): void => {
  if (clientSide && window?.KoreChatSDK) {
    try {
      if (typeof window.KoreChatSDK.showProactiveRVIcon === 'function') {
        window.KoreChatSDK.showProactiveRVIcon(message);
      }
    } catch (error) {
      console.warn('Failed to show proactive icon:', error);
    }
  }
};

export const extractChatUserId = (): string | null => {
  if (clientSide && window?.KoreChatSDK) {
    try {
      const userId = window.KoreChatSDK?.chatWindowInstance?.plugins?.AgentDesktopPlugin?.userInfo?.userId;
      if (userId) {
        return userId;
      }
    } catch (error) {
      console.warn('Failed to extract chat user ID:', error);
    }
  }
  return null;
};

/**
 * Show chat icon with optional configuration for position and proactive display.
 * Supports standard, proactive, horizontal side rail, and vertical side rail modes.
 * @param config - Configuration object for chat icon display
 * @returns true if successfully shown, false otherwise
 */
export const showChatIcon = (config: ChatIconConfig): boolean => {
  if (!clientSide || !window?.KoreChatSDK) {
    return false;
  }

  try {
    if (typeof window.KoreChatSDK.showChatIcon === 'function') {
      window.KoreChatSDK.showChatIcon(config);
      return true;
    } else {
      console.warn('KoreChatSDK.showChatIcon is not available');
    }
  } catch (error) {
    console.warn('Failed to show chat icon:', error);
  }

  return false;
};

/**
 * Hide the chat icon from view.
 * @returns true if successfully hidden, false otherwise
 */
export const hideChatIcon = (): boolean => {
  if (!clientSide || !window?.KoreChatSDK) {
    return false;
  }

  try {
    if (typeof window.KoreChatSDK.hideChatIcon === 'function') {
      window.KoreChatSDK.hideChatIcon();
      return true;
    }
  } catch (error) {
    console.warn('Failed to hide chat icon:', error);
  }

  return false;
};

/**
 * Trigger animation on chat icon with a message.
 * @param animateConfig - Configuration object with message text
 * @returns true if successfully triggered, false otherwise
 */
export const triggerAnimation = (animateConfig?: { message: string }): boolean => {
  if (!animateConfig || !clientSide || !window?.KoreChatSDK) {
    return false;
  }

  try {
    if (typeof window.KoreChatSDK.showMessageAnimationOnChatIcon === 'function') {
      window.KoreChatSDK.showMessageAnimationOnChatIcon({
        trigger_type: 'animate',
        animate: {
          message: animateConfig.message,
        },
      });
      return true;
    }
  } catch (error) {
    console.warn('Failed to trigger chat icon animation:', error);
  }

  return false;
};
